import { ChangeEvent, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import LoopIcon from '@mui/icons-material/Loop';
import { format } from 'date-fns';
import TransparentButton from '@/trio-ui/TransparentButton';
import { CandidateAttachment, FileCategories, UploadedCandidateAttachments } from '@/types/api';
import { formatFileSize, labelByFileCategories } from '@/utils/attachments';
import { onScrollToSmoothly } from '@/utils/global';
import type { LoadingInfo } from './types';
import * as Styles from './PersonAttachmentsPageContent.styles';
import SectionBox from '../SectionBox';

type Props = {
  attachments?: UploadedCandidateAttachments;
  onDeleteAttachment?: (id: string) => void;
  onSelectFile?: (event: ChangeEvent<HTMLInputElement>, category: string) => void;
  uploadingInfo: LoadingInfo;
  readonly?: boolean;
};

const PersonAttachmentsPageContent = ({
  attachments = {},
  onDeleteAttachment,
  onSelectFile,
  uploadingInfo,
  readonly = false,
}: Props): JSX.Element => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Styles.PageGrid>
      <div>
        <Styles.Sidebar
          className="!tw-h-auto"
          sidebarMenus={Object.values(FileCategories).map((category) => ({
            label: labelByFileCategories[category],
            id: `sidebar-${category}`,
            onClick: () => onScrollToSmoothly(category),
          }))}
        />
        {/* <button type="button" className="tw-text-sm tw-text-primary-60">
          + New Type
        </button> */}
      </div>
      <Styles.Content>
        <input
          className="tw-hidden"
          ref={inputRef}
          id="input-file"
          accept="application/pdf,.doc,.docx"
          type="file"
          multiple
          onChange={(event) => onSelectFile?.(event, selectedCategory)}
        />
        {Object.keys(FileCategories).map((category) => (
          <SectionBox
            title={labelByFileCategories[FileCategories[category]]}
            id={category}
            key={category}
          >
            <Styles.Table>
              <thead>
                <tr>
                  <td>File name</td>
                  <td>Attached by</td>
                  <td>Modified</td>
                  <td>Size</td>
                  <td />
                </tr>
              </thead>
              <tbody>
                {(attachments[category] || []).map((file: CandidateAttachment) => (
                  <tr key={file.id}>
                    <td>
                      <a className="tw-text-primary-60" href={file.url} download={file.url}>
                        {file.name}
                      </a>
                    </td>
                    <td>{file.user.name}</td>
                    <td>{format(new Date(file.updatedAt), 'MMM d')}</td>
                    <td>{formatFileSize(parseInt(file.size, 10))}</td>
                    <td className="tw-flex tw-gap-3 tw-justify-end">
                      <TransparentButton
                        icon={DownloadIcon}
                        isBlueButton
                        link
                        href={file.url}
                        download={file.url}
                      >
                        Download
                      </TransparentButton>
                      {!readonly && (
                        <TransparentButton
                          icon={DeleteIcon}
                          isRedButton
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteAttachment?.(file.id);
                          }}
                        >
                          Delete
                        </TransparentButton>
                      )}
                    </td>
                  </tr>
                ))}
                {uploadingInfo.category === category &&
                  Array.from(Array(uploadingInfo.filesLoadingCount).keys()).map((i) => (
                    <tr key={i}>
                      <td>
                        <div className="tw-text-xs tw-leading-[24px] tw-text-secondary-60 tw-flex tw-items-center tw-gap-1">
                          <LoopIcon className="!tw-w-3 !tw-h-3 tw-animate-spin" /> Loading
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Styles.Table>
            {!readonly && (
              <button
                type="button"
                className="tw-text-sm tw-text-primary-60 tw-mt-4"
                onClick={() => {
                  setSelectedCategory?.(category as FileCategories);
                  setTimeout(() => {
                    inputRef.current?.click();
                  }, 100);
                }}
              >
                + Attachment
              </button>
            )}
          </SectionBox>
        ))}
      </Styles.Content>
    </Styles.PageGrid>
  );
};

export default PersonAttachmentsPageContent;
